import React from 'react';
import { ArrowLeft, User, Mail, Phone } from 'lucide-react';
import { formatDate } from '../utils/dataModels';

const StudentDetail = ({ students, goals, studentId, onBack, onNavigate }) => {
  const student = students.find(s => s.id === studentId);
  const studentGoals = goals.filter(g => g.studentId === studentId);

  if (!student) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900">Student not found</h2>
        <button
          onClick={onBack}
          className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to Students
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="p-2 rounded-md text-gray-900 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Back to students list"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {student.firstName} {student.lastName}
            </h1>
            <p className="text-gray-900">{student.grade} Grade</p>
          </div>
        </div>
      </div>

      {/* Student Information */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Student Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border-b border-gray-200 pb-4">
            <label className="block text-xs font-semibold text-gray-900 uppercase tracking-wider mb-1">Full Name</label>
            <p className="text-base font-medium text-gray-900">{student.firstName} {student.lastName}</p>
          </div>
          <div className="border-b border-gray-200 pb-4">
            <label className="block text-xs font-semibold text-gray-900 uppercase tracking-wider mb-1">Grade</label>
            <p className="text-base font-medium text-gray-900">{student.grade}</p>
          </div>
          <div className="border-b border-gray-200 pb-4">
            <label className="block text-xs font-semibold text-gray-900 uppercase tracking-wider mb-1">Disability Category</label>
            <p className="text-base font-medium text-gray-900">{student.disabilityCategory}</p>
          </div>
          <div className="border-b border-gray-200 pb-4">
            <label className="block text-xs font-semibold text-gray-900 uppercase tracking-wider mb-1">Case Manager</label>
            <p className="text-base font-medium text-gray-900">{student.caseManager}</p>
          </div>
          <div className="border-b border-gray-200 pb-4">
            <label className="block text-xs font-semibold text-gray-900 uppercase tracking-wider mb-1">IEP Start Date</label>
            <p className="text-base font-medium text-gray-900">{formatDate(student.iepStartDate)}</p>
          </div>
          <div className="border-b border-gray-200 pb-4">
            <label className="block text-xs font-semibold text-gray-900 uppercase tracking-wider mb-1">IEP End Date</label>
            <p className="text-base font-medium text-gray-900">{formatDate(student.iepEndDate)}</p>
          </div>
        </div>
      </div>

      {/* Parent Contact */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Parent Contact</h2>
        <div className="space-y-3">
          <div className="flex items-center">
            <User size={16} className="text-gray-600 mr-2" />
            <span className="text-sm text-gray-900">{student.parentContact.name}</span>
          </div>
          <div className="flex items-center">
            <Mail size={16} className="text-gray-600 mr-2" />
            <a href={`mailto:${student.parentContact.email}`} className="text-sm text-primary hover:underline">
              {student.parentContact.email}
            </a>
          </div>
          <div className="flex items-center">
            <Phone size={16} className="text-gray-600 mr-2" />
            <a href={`tel:${student.parentContact.phone}`} className="text-sm text-primary hover:underline">
              {student.parentContact.phone}
            </a>
          </div>
        </div>
      </div>

      {/* Goals */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">IEP Goals</h2>
          <button
            onClick={() => onNavigate('goals')}
            className="px-3 py-1 text-xs font-medium text-primary border border-primary rounded hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary"
          >
            View All
          </button>
        </div>
        {studentGoals.length === 0 ? (
          <p className="text-gray-700 text-center py-4">No goals created yet</p>
        ) : (
          <div className="space-y-4">
            {studentGoals.map(goal => (
              <div key={goal.id} className="pl-4 border-l-2 border-gray-200 py-2">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{goal.domain}</h3>
                    <p className="text-sm text-gray-900 mt-1">{goal.description}</p>
                    <p className="text-sm text-gray-900 mt-2">Status: {goal.status}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentDetail;